/**
 * thumbnailService.ts
 *
 * Generates small JPEG thumbnails from media blobs at capture time.
 * Thumbnails are base64 data URLs (~3-8 KB) suitable for in-memory storage
 * and instant display while the full upload runs in the background.
 */

const THUMB_MAX_DIM = 160;
const THUMB_QUALITY = 0.6;

/**
 * Generate a thumbnail data URL from a media blob.
 * Returns null for unsupported types (audio, text).
 */
export async function generateThumbnail(
  blob: Blob,
  mediaType: string,
): Promise<string | null> {
  try {
    if (mediaType === 'photo') return await photoThumbnail(blob);
    if (mediaType === 'video' || mediaType === 'screen') return await videoThumbnail(blob);
    return null;
  } catch {
    return null; // Never block the upload pipeline for a thumbnail
  }
}

/** Resize a photo blob to a small JPEG data URL. */
function photoThumbnail(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = scaled(img.naturalWidth, img.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', THUMB_QUALITY));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/** Extract a frame from a video blob and return a JPEG data URL. */
function videoThumbnail(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const video = document.createElement('video');
    video.playsInline = true;
    video.preload = 'auto';
    video.muted = true;

    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };

    // Timeout: if we can't extract a frame in 3s, give up
    const timer = setTimeout(() => { cleanup(); resolve(null); }, 3000);

    video.onloadeddata = () => {
      // Seek to 0.5s (or 0 for very short clips)
      video.currentTime = Math.min(0.5, video.duration * 0.1);
    };

    video.onseeked = () => {
      clearTimeout(timer);
      const { width, height } = scaled(video.videoWidth, video.videoHeight);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(video, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', THUMB_QUALITY);
      cleanup();
      resolve(dataUrl);
    };

    video.onerror = () => { clearTimeout(timer); cleanup(); resolve(null); };
    video.src = url;
  });
}

/** Calculate scaled dimensions preserving aspect ratio. */
function scaled(srcW: number, srcH: number): { width: number; height: number } {
  if (srcW <= THUMB_MAX_DIM && srcH <= THUMB_MAX_DIM) return { width: srcW, height: srcH };
  const scale = THUMB_MAX_DIM / Math.max(srcW, srcH);
  return {
    width: Math.round(srcW * scale),
    height: Math.round(srcH * scale),
  };
}
